/**
 * @param {{ adminInfo?: object, accessToken?: string }} auth
 * @returns {object}
 */
export function buildLaunchDarklyUserContext(auth) {
  const adminInfo = auth?.adminInfo
  const accessToken = auth?.accessToken

  const baseContext = {
    // LaunchDarkly requires a `key` in order to evaluate/track flags reliably.
    // When user info isn't available yet (login/reset flows), fall back to an anonymous key.
    key: 'anonymous',
    anonymous: !accessToken || !adminInfo,
  }

  if (!accessToken || !adminInfo) {
    return baseContext
  }

  const userName = `${adminInfo.first_name} ${adminInfo.last_name}`

  return {
    ...baseContext,
    key: adminInfo.id,
    user_id: adminInfo.id,
    email: adminInfo.email,
    first_name: adminInfo.first_name,
    last_name: adminInfo.last_name,
    name: userName,
    ...(adminInfo.details ? { phone_number: adminInfo.details.phone_number } : {}),
  }
}

/**
 * @param {object|null|undefined} activeCharityInfo
 * @returns {object|null}
 */
export function buildLaunchDarklyCharityContext(activeCharityInfo) {
  if (!activeCharityInfo?.charity_id) {
    return null
  }

  return {
    key: activeCharityInfo.charity_id,
    charity_id: activeCharityInfo.charity_id,
    charity_name: activeCharityInfo.charity_name,
    name: activeCharityInfo.charity_name,
  }
}

/**
 * Build LaunchDarkly user/multi context from Vuex state (auth + charities modules).
 * @param {{ auth: object, charities: object }} rootState
 * @returns {object}
 */
export function buildLaunchDarklyContextFromState(rootState) {
  const userContext = buildLaunchDarklyUserContext(rootState.auth)
  const charity = buildLaunchDarklyCharityContext(rootState.charities.activeCharityInfo)

  if (!charity) {
    return userContext
  }

  return {
    kind: 'multi',
    user: userContext,
    charity,
  }
}

/** @param {object} context */
export function hasLaunchDarklyCharityContext(context) {
  return context?.kind === 'multi' && Boolean(context.charity)
}

/**
 * Stable cache key for the current LD identity (user + charity).
 * @param {{ auth: object, charities: object }} rootState
 * @returns {string}
 */
export function getLaunchDarklyContextKey(rootState) {
  const userId = rootState.auth?.adminInfo?.id ?? 'anonymous'
  const charityId = rootState.charities?.activeCharityInfo?.charity_id ?? ''
  return `${userId}:${charityId}`
}
